import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, ShieldCheck, Copy, Check, Trash2, Clock, Stethoscope, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TTL_OPTIONS = [
  { key: '1h', label: '1 hour' },
  { key: '24h', label: '24 hours' },
  { key: '7d', label: '7 days' },
];

const buildShareUrl = (token) =>
  `${window.location.origin}/p/clinician/${encodeURIComponent(token)}`;

const formatRelativeExpiry = (iso) => {
  if (!iso) return '';
  try {
    const diffMs = new Date(iso).getTime() - Date.now();
    if (diffMs <= 0) return 'expired';
    const m = Math.floor(diffMs / 60000);
    if (m < 60) return `expires in ${m} min${m === 1 ? '' : 's'}`;
    const h = Math.floor(m / 60);
    if (h < 24) return `expires in ${h} hour${h === 1 ? '' : 's'}`;
    const d = Math.floor(h / 24);
    return `expires in ${d} day${d === 1 ? '' : 's'}`;
  } catch {
    return '';
  }
};

/**
 * Privacy-first modal that lets a user mint a tokenised, expiring,
 * read-only share link of ONLY their health information (allergies,
 * conditions, medications, optional emergency contact). Account data,
 * dashboard analytics, reminders, and settings are never included.
 */
const ClinicianShareModal = ({ open, onClose }) => {
  const [activeShare, setActiveShare] = useState(null); // metadata only, never the token
  const [history, setHistory] = useState([]);
  const [generated, setGenerated] = useState(null);     // includes plaintext token, lives in state only
  const [ttl, setTtl] = useState('24h');
  const [includeEmergency, setIncludeEmergency] = useState(false);
  const [working, setWorking] = useState(false);
  const [copied, setCopied] = useState(false);

  const refreshActive = async () => {
    try {
      const [activeRes, histRes] = await Promise.all([
        axios.get(`${API}/api/clinician-share/active`, { withCredentials: true }),
        axios.get(`${API}/api/clinician-share/history`, { withCredentials: true })
          .catch(() => ({ data: { shares: [] } })),
      ]);
      setActiveShare(activeRes.data?.share || null);
      setHistory(histRes.data?.shares || []);
    } catch {
      setActiveShare(null);
      setHistory([]);
    }
  };

  useEffect(() => {
    if (!open) {
      // Wipe transient state on close — plaintext token never persists.
      setGenerated(null);
      setCopied(false);
      return;
    }
    refreshActive();
  }, [open]);

  const handleGenerate = async () => {
    setWorking(true);
    try {
      const res = await axios.post(
        `${API}/api/clinician-share`,
        { ttl, include_emergency: includeEmergency },
        { withCredentials: true },
      );
      setGenerated(res.data);
      // Active metadata refreshes too so the bottom card stays in sync.
      await refreshActive();
      toast.success('Share link generated. Copy it now — it won\'t be shown again.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate the link. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!id) return;
    setWorking(true);
    try {
      await axios.delete(`${API}/api/clinician-share/${id}`, { withCredentials: true });
      setActiveShare(null);
      setGenerated(null);
      toast.success('Access revoked. The link no longer works.');
      await refreshActive();
    } catch {
      toast.error('Could not revoke. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  // Sweep-revoke EVERY active link in one shot. Useful if the user
  // suspects misuse, lost their device, or simply wants a fresh slate.
  const handleRevokeAll = async () => {
    const confirmed = typeof window !== 'undefined'
      ? window.confirm('Revoke every active clinician share link? This cannot be undone.')
      : true;
    if (!confirmed) return;
    setWorking(true);
    try {
      const res = await axios.post(
        `${API}/api/clinician-share/revoke-all`,
        {},
        { withCredentials: true },
      );
      const n = res.data?.revoked_count ?? 0;
      setActiveShare(null);
      setGenerated(null);
      toast.success(
        n === 0
          ? 'No active links to revoke.'
          : `Revoked ${n} active link${n === 1 ? '' : 's'}.`,
      );
      await refreshActive();
    } catch {
      toast.error('Could not revoke all. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  // Count only ACTIVE rows in the visible history (revoked / expired
  // don't count). Used to gate the bulk sweep button.
  const activeHistoryCount = history.filter((r) => r.status === 'active').length;

  const copyLink = async () => {
    if (!generated?.token) return;
    try {
      await navigator.clipboard.writeText(buildShareUrl(generated.token));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy. Long-press the link to copy manually.');
    }
  };

  // Native system share-sheet (iOS/Android primarily). Falls back to
  // hiding the button entirely on browsers without `navigator.share`.
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const handleNativeShare = async () => {
    if (!generated?.token) return;
    try {
      await navigator.share({
        title: 'HealthLoop · health summary',
        text: 'Read-only health summary (expires automatically).',
        url: buildShareUrl(generated.token),
      });
    } catch (e) {
      // User cancelled the share sheet — silent. Anything else is logged
      // but never surfaced as a noisy toast.
      if (e?.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.warn('navigator.share failed:', e);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="sm:max-w-lg rounded-2xl"
        data-testid="clinician-share-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700">
              <Stethoscope className="h-5 w-5" />
            </span>
            <div>
              <DialogTitle className="text-lg">Share with a clinician</DialogTitle>
              <DialogDescription className="mt-0.5">
                Create a temporary, read-only summary of your health information.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* If no link yet: consent + options */}
        {!generated && (
          <div className="space-y-4 pt-2">
            {/* Consent block */}
            <Card className="border border-emerald-100 bg-emerald-50/60 rounded-xl">
              <CardContent className="p-4 text-sm text-emerald-900 leading-relaxed">
                You are about to generate a temporary read-only health
                summary link for sharing with a clinician. The link only
                shows your <strong>allergies</strong>, <strong>medical conditions</strong>, and{' '}
                <strong>medications</strong>. It does not expose your account,
                reminders, or dashboard data. Anyone with the link can view
                this summary until it expires or you revoke access.
              </CardContent>
            </Card>

            {/* TTL selector */}
            <div>
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Link expires after
              </p>
              <div
                role="tablist"
                className="inline-flex p-1 rounded-full bg-gray-100"
                data-testid="clinician-share-ttl-switcher"
              >
                {TTL_OPTIONS.map((opt) => {
                  const active = ttl === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setTtl(opt.key)}
                      className={`px-3.5 h-9 text-xs font-medium rounded-full transition-colors ${
                        active ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                      }`}
                      data-testid={`clinician-share-ttl-${opt.key}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional emergency contact */}
            <div className="flex items-start justify-between gap-3 rounded-xl border border-gray-100 bg-white p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  Include emergency contact
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Optional. Only included if you've added one to your profile.
                </p>
              </div>
              <Switch
                checked={includeEmergency}
                onCheckedChange={setIncludeEmergency}
                disabled={working}
                data-testid="clinician-share-emergency-toggle"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={working}
                data-testid="clinician-share-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={working}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="clinician-share-generate-btn"
              >
                {working && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Generate share link
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* After generation: show link, copy, revoke */}
        {generated && (
          <div className="space-y-4 pt-2" data-testid="clinician-share-result">
            <Card className="border border-emerald-100 bg-emerald-50/60 rounded-xl">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-emerald-900 text-xs font-semibold uppercase tracking-wide">
                  <ShieldCheck className="h-3.5 w-3.5" /> Share link is live
                </div>
                <p className="text-sm text-emerald-900 leading-relaxed">
                  Copy the link below and send it to your clinician through a
                  channel you trust. <strong>This is the only time you'll
                  see the full link.</strong>
                </p>
              </CardContent>
            </Card>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-center gap-2">
              <code
                className="flex-1 text-xs sm:text-sm text-gray-800 break-all leading-snug"
                data-testid="clinician-share-link"
              >
                {buildShareUrl(generated.token)}
              </code>
              {canNativeShare && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleNativeShare}
                  className="shrink-0 h-9"
                  aria-label="Share via system share sheet"
                  data-testid="clinician-share-native-btn"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                onClick={copyLink}
                className={`shrink-0 ${
                  copied ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-gray-900 hover:bg-black'
                }`}
                data-testid="clinician-share-copy-btn"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>

            <p className="text-xs text-gray-500 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {formatRelativeExpiry(generated.expires_at)}
            </p>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                onClick={() => handleRevoke(generated.id)}
                disabled={working}
                data-testid="clinician-share-revoke-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Revoke access
              </Button>
              <Button type="button" onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Existing active share (when nothing was just generated) */}
        {!generated && activeShare && (
          <div
            className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3"
            data-testid="clinician-share-existing"
          >
            <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-1">
              You have a share link active
            </p>
            <p className="text-xs text-amber-900 leading-relaxed">
              Ends in …{activeShare.token_suffix} · {formatRelativeExpiry(activeShare.expires_at)}.
              Generating a new one will revoke this one.
            </p>
            <div className="mt-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-rose-700 border-rose-200 hover:bg-rose-50 h-8"
                onClick={() => handleRevoke(activeShare.id)}
                disabled={working}
                data-testid="clinician-share-existing-revoke-btn"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Revoke now
              </Button>
            </div>
          </div>
        )}

        {/* Recent shares — last 5 minted, useful for audit. Plaintext tokens
            are not retrievable, only suffix + status + dates. */}
        {!generated && history.length > 0 && (
          <div
            className="mt-4 pt-4 border-t border-gray-100"
            data-testid="clinician-share-history"
          >
            <div className="flex items-center justify-between mb-2 gap-2">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                Recent shares
              </p>
              {activeHistoryCount >= 2 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleRevokeAll}
                  disabled={working}
                  className="h-7 text-[11px] px-2.5 text-rose-700 border-rose-200 hover:bg-rose-50"
                  data-testid="clinician-share-revoke-all-btn"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Revoke all active
                </Button>
              )}
            </div>
            <ul className="space-y-1.5">
              {history.map((row) => {
                const status = row.status; // active | revoked | expired
                const dot = {
                  active: 'bg-emerald-500',
                  revoked: 'bg-rose-400',
                  expired: 'bg-gray-300',
                }[status] || 'bg-gray-300';
                const label = {
                  active: 'Active',
                  revoked: 'Revoked',
                  expired: 'Expired',
                }[status] || status;
                let createdLabel = '';
                try {
                  createdLabel = new Date(row.created_at).toLocaleDateString(undefined, {
                    month: 'short', day: 'numeric',
                  });
                } catch { /* ignore */ }
                return (
                  <li
                    key={row.id}
                    className="flex items-center gap-2 text-xs"
                    data-testid={`clinician-share-history-row-${row.id}`}
                  >
                    <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden="true" />
                    <span className="text-gray-700 tabular-nums">{createdLabel}</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-700">…{row.token_suffix}</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-700">{row.ttl_key}</span>
                    <span className="ml-auto text-gray-500">{label}</span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-2 text-[10px] text-gray-400 italic">
              Showing the last {history.length} share{history.length === 1 ? '' : 's'}. Plaintext links are never stored.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClinicianShareModal;
